/* Harper & Stone — cart.
 *
 * One module for both surfaces. Every mutation goes through the same two
 * functions, and both re-render from Liquid through the Section Rendering
 * API rather than rebuilding markup here — so the drawer and the page cannot
 * drift apart, and a line looks the same in both.
 *
 * Without JavaScript the page's form still posts to /cart and the quantity
 * inputs still apply. Nothing here is load-bearing.
 */

const DRAWER_SECTION = 'hs-cart-drawer';
const DEBOUNCE = 400;

/* One in-flight request at a time. Two steppers pressed quickly would
 * otherwise race, and the slower response would overwrite the newer state. */
let inFlight = null;

async function postCart(url, body) {
  inFlight?.abort();
  inFlight = new AbortController();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal: inFlight.signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Cart update failed');
  }
  return res.json();
}

/* Re-render both surfaces from Liquid. Whichever is on the page gets the
 * section's own markup back; the other simply is not there. */
async function refresh() {
  const jobs = [];

  const drawer = document.querySelector('[data-cart-drawer-body]');
  if (drawer) {
    jobs.push(
      fetch(`${window.location.pathname}?section_id=${DRAWER_SECTION}`)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => {
          if (!html) return;
          const next = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-cart-drawer-body]');
          if (next) drawer.replaceWith(next);
        }),
    );
  }

  const page = document.querySelector('[data-cart-page]');
  if (page) {
    jobs.push(
      fetch(window.location.pathname)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => {
          if (!html) return;
          const next = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-cart-page]');
          if (!next) return;
          page.innerHTML = next.innerHTML;
          /* The fresh markup carries the no-script update button again,
           * without the `hidden` that connectedCallback set: the host element
           * survives the re-render, so it never reconnects. Hide it here as
           * well, or it reappears after the first change of quantity. */
          for (const el of page.querySelectorAll('[data-cart-update]')) el.hidden = true;
        }),
    );
  }

  await Promise.all(jobs);
  updateBadge();
}

/* The header badge is outside both surfaces, so it is updated from the cart
 * itself rather than from either re-render. */
async function updateBadge() {
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  } catch {
    /* A stale badge is not worth an error. */
  }
}

/* The drawer.
 *
 * A native <dialog> opened with showModal(), which brings focus trapping,
 * Escape and inertness of the page behind it without a line of script. */
class HsCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-drawer]');
    if (!this.dialog) return;

    // Any control in the drawer, at any time: the contents are replaced on
    // every cart change, so listeners are delegated rather than bound to
    // elements that will not exist a moment from now.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }

      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.#change(Number(remove.dataset.cartRemove), 0);
        return;
      }

      const step = e.target.closest('[data-cart-step]');
      if (step) {
        e.preventDefault();
        const line = Number(step.dataset.line);
        const input = this.querySelector(`[data-cart-qty][data-line="${line}"]`);
        const next = Math.max(0, (Number(input?.value) || 0) + Number(step.dataset.cartStep));
        this.#change(line, next);
      }
    });

    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      this.#change(Number(input.dataset.line), Math.max(0, Number(input.value) || 0));
    });

    // A click on the backdrop closes. <dialog> reports those as clicks on
    // itself, so compare against the dialog's own box.
    this.dialog.addEventListener('click', (e) => {
      if (e.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) this.close();
    });

    this.dialog.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      this.opener?.focus();
      this.opener = null;
    });

    // Opened by the PDP's add-to-cart, and by the header bag when the cart
    // has something in it.
    document.addEventListener('cart:added', (e) => this.open(e.detail?.key));

    for (const link of document.querySelectorAll('a[href$="/cart"], a[href*="/cart?"]')) {
      link.addEventListener('click', (e) => {
        // The full cart page stays reachable: only intercept when there is
        // something to show, and never when the shopper is already on it.
        if (document.querySelector('[data-cart-page]')) return;
        if (!document.querySelector('[data-cart-items]')) return;
        e.preventDefault();
        this.opener = link;
        this.open();
      });
    }
  }

  async open(addedKey) {
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
    await refresh();
    if (addedKey) this.#highlight(addedKey);
  }

  close() {
    this.dialog?.close();
  }

  /* The one move neither reference makes.
   *
   * Both open the drawer at the top, which is right for one item and wrong
   * for six: the line just added is the only thing the shopper is looking
   * for, and on a full bag it is below the fold. Scroll to it and mark it
   * briefly — it answers "did that work?" without a toast. */
  #highlight(key) {
    const line = this.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (!line) return;

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    line.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    line.dataset.added = 'true';
    setTimeout(() => delete line.dataset.added, 1400);
  }

  async #change(line, quantity) {
    clearTimeout(this.timer);
    this.timer = setTimeout(async () => {
      this.dialog?.setAttribute('aria-busy', 'true');
      try {
        await postCart('/cart/change.js', { line, quantity });
        await refresh();
      } catch (err) {
        if (err.name !== 'AbortError') this.#error(err.message);
      } finally {
        this.dialog?.removeAttribute('aria-busy');
      }
    }, DEBOUNCE);
  }

  #error(message) {
    const body = this.querySelector('[data-cart-drawer-body]');
    if (!body) return;
    let el = body.querySelector('[data-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'cdraw__error';
      el.setAttribute('role', 'alert');
      el.dataset.cartError = '';
      body.prepend(el);
    }
    el.textContent = message;
  }
}

/* The cart page. Same mutations, same re-render — only the surface differs. */
class HsCart extends HTMLElement {
  connectedCallback() {
    // With a script, the stepper applies changes as they are made, so the
    // explicit update button is redundant. It is rendered in the form rather
    // than inside <noscript> — script cannot reach into that element — and
    // hidden here, so it exists exactly when nothing is listening.
    for (const el of this.querySelectorAll('[data-cart-update]')) el.hidden = true;

    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.#change(Number(remove.dataset.cartRemove), 0);
        return;
      }

      const step = e.target.closest('[data-cart-step]');
      if (step) {
        e.preventDefault();
        const line = Number(step.dataset.line);
        const input = this.querySelector(`[data-cart-qty][data-line="${line}"]`);
        const next = Math.max(0, (Number(input?.value) || 0) + Number(step.dataset.cartStep));
        this.#change(line, next);
      }
    });

    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      this.#change(Number(input.dataset.line), Math.max(0, Number(input.value) || 0));
    });
  }

  async #change(line, quantity) {
    clearTimeout(this.timer);
    this.timer = setTimeout(async () => {
      this.setAttribute('aria-busy', 'true');
      try {
        await postCart('/cart/change.js', { line, quantity });
        await refresh();
      } catch (err) {
        if (err.name !== 'AbortError') location.reload();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }, DEBOUNCE);
  }
}

if (!customElements.get('hs-cart-drawer')) customElements.define('hs-cart-drawer', HsCartDrawer);
if (!customElements.get('hs-cart')) customElements.define('hs-cart', HsCart);

export { HsCartDrawer, HsCart, refresh, updateBadge };
